/**
 * Sticky Video Tab
 * A floating trigger that opens a drawer with an embedded video.
 * Uses native event listeners (not theme Component class).
 */
class StickyVideoTab extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('dialog[ref="drawer"]');
    this.videoContainer = this.querySelector('[ref="videoContainer"]');
    this.videoType = this.dataset.videoType;
    this.videoId = this.dataset.videoId;
    this.iframeLoaded = false;

    // Trigger button click
    const trigger = this.querySelector('.sticky-video-tab__trigger');
    if (trigger) {
      trigger.addEventListener('click', () => this.openDrawer());
    }

    // Close button click
    const closeBtn = this.querySelector('.sticky-video-tab__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeDrawer());
    }

    // Mobile scroll visibility
    this.trigger = trigger;
    this.handleScrollVisibility();

    // Close on backdrop click
    if (this.drawer) {
      this.drawer.addEventListener('click', (e) => {
        if (e.target === this.drawer) {
          this.closeDrawer();
        }
      });

      // Close on Escape
      this.drawer.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeDrawer();
      });
    }
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.showModal();
    this.loadVideo();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.add('dialog-closing');

    const onEnd = () => {
      this.drawer.classList.remove('dialog-closing');
      this.drawer.close();
      this.destroyVideo();
    };

    this.drawer.addEventListener('animationend', onEnd, { once: true });

    // Fallback if no animation plays
    setTimeout(onEnd, 400);
  }

  loadVideo() {
    if (!this.videoContainer || !this.videoId || this.iframeLoaded) return;

    let src = '';
    if (this.videoType === 'youtube') {
      src = `https://www.youtube.com/embed/${this.videoId}?rel=0&modestbranding=1&autoplay=1`;
    } else if (this.videoType === 'vimeo') {
      src = `https://player.vimeo.com/video/${this.videoId}?autoplay=1`;
    }

    if (src) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.title = this.querySelector('.sticky-video-tab__drawer-title')?.textContent || 'Video';
      iframe.frameBorder = '0';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      this.videoContainer.appendChild(iframe);
      this.iframeLoaded = true;
    }
  }

  destroyVideo() {
    if (!this.videoContainer) return;
    this.videoContainer.innerHTML = '';
    this.iframeLoaded = false;
  }

  handleScrollVisibility() {
    const mq = window.matchMedia('(max-width: 749px)');
    const footer = document.querySelector('footer, .footer, #shopify-section-footer, [id*="footer"]');

    const updateVisibility = (isMobile) => {
      const scrollY = window.scrollY;
      const inFooter = footer ? (scrollY + window.innerHeight) >= footer.offsetTop : false;

      if (isMobile) {
        const threshold = window.innerHeight * 0.8;
        if (scrollY <= threshold || inFooter) {
          this.classList.add('is-hidden');
        } else {
          this.classList.remove('is-hidden');
        }
      } else {
        if (inFooter) {
          this.classList.add('is-hidden');
        } else {
          this.classList.remove('is-hidden');
        }
      }
    };

    this._scrollHandler = () => updateVisibility(mq.matches);
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
    mq.addEventListener('change', () => this._scrollHandler());

    // Initial check
    this._scrollHandler();
  }
}

if (!customElements.get('sticky-video-tab')) {
  customElements.define('sticky-video-tab', StickyVideoTab);
}
